import React, { useEffect, useState } from 'react';
import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined, InfoCircleOutlined, CloseOutlined } from '@ant-design/icons';
import './toast.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';
export type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';

export interface ToastProps {
  id?: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
  position?: ToastPosition;
  showCloseButton?: boolean;
  onClose?: () => void;
  className?: string;
}

export interface ToastNotificationProps extends ToastProps {
  isVisible: boolean;
  onAnimationComplete?: () => void;
}

const getToastIcon = (type: ToastType) => {
  switch (type) {
    case 'success':
      return <CheckCircleOutlined className="toast-icon toast-icon--success" />;
    case 'error':
      return <CloseCircleOutlined className="toast-icon toast-icon--error" />;
    case 'warning':
      return <WarningOutlined className="toast-icon toast-icon--warning" />;
    case 'info':
      return <InfoCircleOutlined className="toast-icon toast-icon--info" />;
    default:
      return <InfoCircleOutlined className="toast-icon toast-icon--info" />;
  }
};

export const ToastNotification: React.FC<ToastNotificationProps> = ({
  type,
  title,
  message,
  duration = 4000,
  position = 'top-right',
  showCloseButton = true,
  isVisible,
  onClose,
  onAnimationComplete,
  className = '',
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(isVisible);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      // Small delay to trigger enter animation
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      // Wait for exit animation to complete
      const timer = setTimeout(() => {
        setShouldRender(false);
        onAnimationComplete?.();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onAnimationComplete]);

  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose?.();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!shouldRender) return null;

  return (
    <div
      className={`toast-notification toast-notification--${type} toast-notification--${position} ${isAnimating ? 'toast-notification--visible' : ''} ${className}`}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="toast-notification__content">
        {getToastIcon(type)}

        <div className="toast-notification__text">
          {title && (
            <div className="toast-notification__title">
              {title}
            </div>
          )}

          <div className="toast-notification__message">
            {message}
          </div>
        </div>

        {showCloseButton && (
          <button
            onClick={onClose}
            className="toast-notification__close"
            aria-label="Close notification"
          >
            <CloseOutlined className="toast-notification__close-icon" />
          </button>
        )}
      </div>
    </div>
  );
};

// Toast container for managing multiple toasts
export interface ToastContainerProps {
  toasts: (ToastProps & { id: string; isVisible: boolean })[];
  onRemoveToast: (id: string) => void;
  position?: ToastPosition;
  className?: string;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onRemoveToast,
  position = 'top-right',
  className = '',
}) => {
  return (
    <div className={`toast-container toast-container--${position} ${className}`}>
      {toasts.map((toast) => (
        <div key={toast.id} className="toast-container__item">
          <ToastNotification
            {...toast}
            position={position}
            onClose={() => onRemoveToast(toast.id)}
            onAnimationComplete={() => {
              if (!toast.isVisible) {
                onRemoveToast(toast.id);
              }
            }}
          />
        </div>
      ))}
    </div>
  );
};

// Hook for managing toasts
export const useToast = () => {
  const [toasts, setToasts] = useState<(ToastProps & { id: string; isVisible: boolean })[]>([]);

  const addToast = (toastData: Omit<ToastProps, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { ...toastData, id, isVisible: true }]);
    return id;
  };

  const removeToast = (id: string) => {
    setToasts(prev =>
      prev.map(toast =>
        toast.id === id ? { ...toast, isVisible: false } : toast
      )
    );
  };

  const clearAllToasts = () => {
    setToasts(prev =>
      prev.map(toast => ({ ...toast, isVisible: false }))
    );
  };

  const actuallyRemoveToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    actuallyRemoveToast,
  };
};

// Convenience functions for different toast types
export const toast = {
  success: (message: string, options?: Partial<ToastProps>) => ({
    type: 'success' as const,
    message,
    ...options,
  }),
  error: (message: string, options?: Partial<ToastProps>) => ({
    type: 'error' as const,
    message,
    ...options,
  }),
  warning: (message: string, options?: Partial<ToastProps>) => ({
    type: 'warning' as const,
    message,
    ...options,
  }),
  info: (message: string, options?: Partial<ToastProps>) => ({
    type: 'info' as const,
    message,
    ...options,
  }),
};
